/**
 * Helpers for reading and writing user_notification_preferences rows.
 *
 * One row per (user_id, story_id). Created at story creation time with the
 * frequency picked in CreateStoryModal; channel + preferred_hour are
 * populated/updated in NotificationSetupScreen and StoryNotificationSettings.
 */

import { supabase } from './supabase';
import type { NotificationFrequency } from '../components/notifications/FrequencyPicker';

export type NotificationChannel = 'push' | 'email';
export type NotificationRole = 'protagonist' | 'inviter';

export interface NotificationPreferences {
    id: string;
    user_id: string;
    story_id: string;
    frequency: NotificationFrequency;
    channel: NotificationChannel;
    preferred_hour: number;
    timezone: string;
    paused_until: string | null;
    last_notified_at: string | null;
    last_copy_index: number;
    role: NotificationRole;
    created_at: string;
    updated_at: string;
}

/**
 * Best-effort detection of the user's IANA timezone via Intl. Falls back to
 * Europe/Madrid (the Ethernal default region) if detection fails.
 */
export function detectUserTimezone(): string {
    try {
        return (
            Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Madrid'
        );
    } catch {
        return 'Europe/Madrid';
    }
}

/**
 * Insert or update the preference row for the current user × given story.
 * Uses upsert on the (user_id, story_id) unique constraint so callers don't
 * need to know whether a row already exists.
 */
export async function upsertPreferences(
    storyId: string,
    fields: Partial<
        Pick<
            NotificationPreferences,
            | 'frequency'
            | 'channel'
            | 'preferred_hour'
            | 'timezone'
            | 'paused_until'
            | 'role'
        >
    >,
): Promise<{ success: boolean; error?: string }> {
    const {
        data: { user },
        error: authError,
    } = await supabase.auth.getUser();

    if (authError || !user) {
        return { success: false, error: 'Usuario no autenticado' };
    }

    // Order matters: spread `fields` FIRST, then layer the
    // client-detected default for `timezone` only when the caller didn't
    // pass one. Otherwise a caller passing { timezone: undefined }
    // would null out the column. This is the cause of the 2026-05-10
    // "preferred_hour interpreted as UTC" bug — some rows were saved
    // with timezone='UTC' and the cron fired at UTC hour instead of
    // Madrid local hour.
    const payload = {
        user_id: user.id,
        story_id: storyId,
        ...fields,
        timezone: fields.timezone ?? detectUserTimezone(),
    };

    const { error } = await supabase
        .from('user_notification_preferences')
        .upsert(payload, { onConflict: 'user_id,story_id' });

    if (error) {
        console.error(
            '[notificationPreferences] Upsert failed:',
            error,
        );
        return { success: false, error: error.message };
    }

    return { success: true };
}

/**
 * Persist a web push subscription for the current user. Idempotent on the
 * subscription endpoint (upserts so the same browser/device can re-run
 * subscribe without creating duplicate rows). Also flips the latest
 * preference rows for this user to channel='push' so the cron picks them
 * up via push instead of email.
 */
export async function savePushSubscription(
    subscription: PushSubscription,
): Promise<{ success: boolean; error?: string }> {
    const {
        data: { user },
        error: authError,
    } = await supabase.auth.getUser();

    if (authError || !user) {
        return { success: false, error: 'Usuario no autenticado' };
    }

    const json = subscription.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        return { success: false, error: 'Subscription inválida' };
    }

    const { error } = await supabase
        .from('push_subscriptions')
        .upsert(
            {
                user_id: user.id,
                endpoint: json.endpoint,
                p256dh_key: json.keys.p256dh,
                auth_key: json.keys.auth,
                user_agent: (navigator.userAgent || '').slice(0, 500),
            },
            { onConflict: 'endpoint' },
        );

    if (error) {
        console.error(
            '[notificationPreferences] savePushSubscription failed:',
            error,
        );
        return { success: false, error: error.message };
    }
    return { success: true };
}

/**
 * Fetch the preference row for the current user × given story. Returns null
 * if no row exists yet (e.g., user is mid-flow before the row was inserted).
 */
export async function fetchPreferences(
    storyId: string,
): Promise<NotificationPreferences | null> {
    const {
        data: { user },
    } = await supabase.auth.getUser();

    if (!user) return null;

    const { data, error } = await supabase
        .from('user_notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .eq('story_id', storyId)
        .maybeSingle();

    if (error) {
        console.error(
            '[notificationPreferences] Fetch failed:',
            error,
        );
        return null;
    }

    return data as NotificationPreferences | null;
}

/**
 * Fetch every preference row for the current user. Used by the activate-push
 * landing screen to offer "apply to all stories" once the user has installed
 * the PWA and granted notification permission.
 */
export async function fetchAllPreferences(): Promise<NotificationPreferences[]> {
    const {
        data: { user },
    } = await supabase.auth.getUser();

    if (!user) return [];

    const { data, error } = await supabase
        .from('user_notification_preferences')
        .select('*')
        .eq('user_id', user.id);

    if (error) {
        console.error(
            '[notificationPreferences] fetchAllPreferences failed:',
            error,
        );
        return [];
    }
    return (data || []) as NotificationPreferences[];
}

/**
 * Bulk-update the channel on every preference row for the current user.
 * Used after a successful PWA install + push subscribe so the user can opt
 * every story into push without revisiting each one.
 */
export async function updateAllPreferencesChannel(
    channel: NotificationChannel,
): Promise<{ success: boolean; error?: string }> {
    const {
        data: { user },
        error: authError,
    } = await supabase.auth.getUser();

    if (authError || !user) {
        return { success: false, error: 'Usuario no autenticado' };
    }

    const { error } = await supabase
        .from('user_notification_preferences')
        .update({ channel })
        .eq('user_id', user.id);

    if (error) {
        console.error(
            '[notificationPreferences] updateAllPreferencesChannel failed:',
            error,
        );
        return { success: false, error: error.message };
    }
    return { success: true };
}
